import {useNavigate} from 'react-router-dom'
import {Dispatch} from 'redux'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {useEffect, useState} from 'react'
import {PageTitle} from '../../../_metronic/layout/core'
import {RootState} from '../../../setup'
import * as actions from '../../../setup/redux/inspection_companies/actions'
import {Loader} from '../../services/Loader'
import {CustomForm} from '../inspection_companies/pages/CustomForm'
import {getInspectionCompanies, getInspectionCompanyById} from '../../../setup/redux/auth/actions'
import {requestAccountDeletion} from '../../../setup/redux/data_anonymization/actions'
import Dialog from '../../../_metronic/dialogs/CommonDialog'
import CommonDialogBody from '../../../_metronic/dialogs/CommonDialogBody'
import {Button} from 'react-bootstrap'

const initInspectionCompany = {
  id: undefined,
  name: '',
  description: '',
  email: '',
  contact_person: '',
  country_code: '',
  phone: '',
  fax: '',
  location: '',
  latitude: '',
  longitude: '',
  logo: undefined,
  video: undefined,
  is_active: true,
}

export const UpdateCompanyProfile = () => {
  const navigate = useNavigate()
  const dispatch: Dispatch<any> = useDispatch()
  const {inspectionCompany, user, actionsLoading, active_inspection_company}: any =
    useSelector<RootState>(
      (state) => ({
        actionsLoading: state.common.actionsLoading,
        inspectionCompany: state.inspectionCompanies.inspectionCompany,
        user: state.auth.user,
        active_inspection_company: state.auth.active_inspection_company,
      }),
      shallowEqual
    )

  const companyID = user.is_root ? user.inspection_company : active_inspection_company.id
  const isDecentCompany = active_inspection_company?.slug === 'decent'
  const [isOpenDeleteCompanyDialog, setIsOpenDeleteCompanyDialog] = useState<boolean>(false)
  const [deleteCompanyLoading, setDeleteCompanyLoading] = useState<boolean>(false)
  const [deleteRequestSubmitted, setDeleteRequestSubmitted] = useState<boolean>(false)

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (companyID) {
        try {
          const companyDataResponse: any = await dispatch(
            actions.getInspectionCompanyById(companyID, 'edit')
          )
          if (
            companyDataResponse?.inspection_company?.data_anonymization_company_requests &&
            Array.isArray(
              companyDataResponse.inspection_company.data_anonymization_company_requests
            )
          ) {
            const hasPendingRequest =
              companyDataResponse.inspection_company.data_anonymization_company_requests.some(
                (request: any) => request.status === 'pending'
              )
            if (hasPendingRequest) {
              setDeleteRequestSubmitted(true)
            }
          }
        } catch (error) {
          console.error('Error fetching company data:', error)
        }
      }
    }

    fetchCompanyData()
  }, [companyID])

  const saveInspectionCompany = async (values: any) => {
    let response: any = await dispatch(actions.updateInspectionCompany(values))
    if (response?.success) {
      dispatch(getInspectionCompanies(companyID))
      navigate(`/`)
      dispatch(getInspectionCompanyById(companyID, 'edit'))
    }
  }

  const handleDeleteCompany = async () => {
    setDeleteCompanyLoading(true)
    try {
      const response: any = await dispatch(
        requestAccountDeletion({request_type: 'COMPANY', target_company_id: companyID})
      )
      if (response?.success) {
        setIsOpenDeleteCompanyDialog(false)
        setDeleteRequestSubmitted(true)
      }
    } catch (error) {
      console.error('Error deleting company:', error)
    } finally {
      setDeleteCompanyLoading(false)
    }
  }

  const handleCloseDeleteCompanyDialog = () => {
    setIsOpenDeleteCompanyDialog(false)
  }

  return (
    <>
      {actionsLoading && <Loader />}
      <PageTitle>Manage Company Profile</PageTitle>
      <CustomForm
        id={companyID}
        inspectionCompany={inspectionCompany || initInspectionCompany}
        saveInspectionCompany={saveInspectionCompany}
        isCompanyProfile={true}
      />

      {!isDecentCompany && (
        <div className='card gutter-b'>
          <div className='card-body'>
            <div className='row'>
              <div className='col-md-12'>
                <div className='form-group m-0'>
                  <div className='d-flex flex-column align-items-start'>
                    <button
                      type='button'
                      className={`btn btn-link text-danger p-0 mb-2 text-decoration-none fw-semibold ${
                        deleteRequestSubmitted ? 'opacity-50' : ''
                      }`}
                      onClick={() => !deleteRequestSubmitted && setIsOpenDeleteCompanyDialog(true)}
                      disabled={deleteCompanyLoading || deleteRequestSubmitted}
                    >
                      Delete Company Account
                    </button>
                    <span className='text-muted fst-italic small'>
                      {deleteRequestSubmitted
                        ? 'Your company delete request has been submitted successfully.'
                        : 'Raise a request to the Admin to delete your company account and associated data.'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Dialog
        show={isOpenDeleteCompanyDialog}
        onHide={handleCloseDeleteCompanyDialog}
        body={
          <CommonDialogBody
            title='Are you sure you want to delete your company account?'
            text='This action is irreversible. Your company account deletion request will be sent for administrator approval.'
          />
        }
        footer={
          <div className='d-flex flex-column-reverse flex-lg-row align-items-center justify-content-center gap-4 w-100 m-0 pt-0'>
            <Button
              variant='secondary'
              onClick={handleCloseDeleteCompanyDialog}
              className='dialog-btn dialog-btn-secondary'
              disabled={deleteCompanyLoading}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleDeleteCompany}
              className='dialog-btn btn btn-primary'
              disabled={deleteCompanyLoading}
            >
              Delete Company Account
            </Button>
          </div>
        }
        modalClassName='delete-dialog'
        headerClassname='border-0 p-0'
        size='lg'
        centered
        backdrop='static'
        keyboard={false}
      />
    </>
  )
}
